/**
 * Оформление партнёра.
 *
 * С сервера приходят не стили, а значения: цвета, шрифт, ширина макета.
 * Сам CSS собран в бандл и лежит рядом с виджетом. Разница важна: если бы
 * оформление грузилось таблицей стилей, сбой сети означал бы голый HTML.
 * Здесь же при недоступной теме виджет отрисуется темой по умолчанию —
 * не в цветах заказчика, но работоспособно.
 *
 * Ключ темы — это имя без приставки `--ls-color-`, чтобы в базе лежало
 * `accent`, а не `--ls-color-accent`: тему заполняет человек, а не код.
 */

/** Цвета: ключ темы → переменная. */
const COLORS: Record<string, string> = {
  bg: '--ls-color-bg',
  surface: '--ls-color-surface',
  'surface-raised': '--ls-color-surface-raised',
  'surface-sunken': '--ls-color-surface-sunken',
  stage: '--ls-color-stage',
  control: '--ls-color-control',
  accent: '--ls-color-accent',
  'accent-ink': '--ls-color-accent-ink',
  'accent-wash': '--ls-color-accent-wash',
  'accent-hi': '--ls-color-accent-hi',
  text: '--ls-color-text',
  'text-muted': '--ls-color-text-muted',
  'header-from': '--ls-color-header-from',
  'header-to': '--ls-color-header-to',
};

/** Остальное: шрифт и прочие значения, которые задаются как есть. */
const PLAIN: Record<string, string> = {
  font: '--ls-font',
  'radius-card': '--ls-radius-card',
};

/**
 * Числовые настройки темы, которые нужны не таблице стилей, а коду:
 * ширина макета определяет масштаб и порог узкой раскладки.
 */
export interface ThemeMetrics {
  designWidth?: number;
  mobileWidth?: number;
}

export type Theme = Record<string, string>;

/** Прочитать число из темы. Мусор игнорируем: тему заполняет человек. */
function numberOf(theme: Theme, key: string): number | undefined {
  const raw = theme[key];
  if (raw === undefined) return undefined;

  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : undefined;
}

export function metricsOf(theme: Theme): ThemeMetrics {
  return {
    designWidth: numberOf(theme, 'design-width'),
    mobileWidth: numberOf(theme, 'mobile-width'),
  };
}

/**
 * Разложить тему по переменным корневого узла.
 *
 * Неизвестные ключи пропускаем молча и намеренно: тему правят в базе,
 * и опечатка в ключе не должна ронять витрину партнёра.
 */
export function applyTheme(root: HTMLElement, theme: Theme): void {
  for (const [key, value] of Object.entries(theme)) {
    const variable = COLORS[key] ?? PLAIN[key];
    if (variable) root.style.setProperty(variable, value);
  }
}
